<template>
  <div :class="classes">
    <c-avatar
      class="avatar"
      hide-tooltip
      :is-active-user="isActiveUser"
      :src="userImage"
      :name-placeholder="userInitials"
    />

    <section class="content">
      <div class="name-header">
        <c-typo
          class="user"
          type="h2"
          opacity="base-80"
          weight="medium"
          color="text"
        >
          <span
            class="user-name"
            v-tooltip.ellipsis.dark
          >
            {{ userName }}
          </span>

          <span
            v-if="messageInfo.length"
            class="info message-info"
            @mouseenter="onShowTooltip($event, messageInfo)"
            @mouseleave="onHideTooltip"
          >
            <c-icon
              class="icon-info"
              size="20"
              icon="info-information-circle"
            />
          </span>

          <span
            v-if="companyInfo.length"
            :class="[ 'info', {
              'message-info': !messageInfo.length
            } ]"
            @mouseenter="onShowTooltip($event, companyInfo)"
            @mouseleave="onHideTooltip"
          >
            <c-icon
              class="icon-info"
              size="20"
              icon="hotel-building"
            />
          </span>

          <c-info-list-tooltip
            dark
            position="top"
            :max-width="300"
            text-align="left"
            element-spacing="5"
            :show="showTooltip"
            :target="tooltipTarget"
            :info-list="infoList"
          />
        </c-typo>

        <c-actions-button
          v-if="userAction"
          data-testid="actions-button"
          :class="actionClasses"
          iconSize="18"
          buttonSize="30"
          :items="actions"
          v-on=$listeners
        />

        <c-form-builder
          v-if="hasEdition"
          class="form"
          ref="form"
          no-actions
          show-overflow
          no-bottom-shadow
          no-upper-shadow
          :schema="schema"
          v-model="value"
          :errors="errors"
          show-errors-when-changed
          @submit="completeEdition"
          @keydown.native.enter="handleEnter"
          @keydown.native.esc="closeEdition"
        />

        <c-typo
          v-if="hasEdition && !isMobile"
          italic
          color="text"
          type="body-1"
          opacity="base-30"
          class="form-text"
        >{{ text }}</c-typo>

        <div v-else class="group-message">
          <c-markdown-message
            v-if="shouldRenderMarkdown"
            :message="message"
            class="message"
          />

          <c-typo v-else class="message" type="body-1">
            <template v-for="(message, index) in formattedMessage">
              {{ message }}
              <br
                v-if="index < formattedMessage.length - 1"
                :key="`br-${index}`"
              />
            </template>
          </c-typo>
        </div>
      </div>

      <footer :class="footerClasses">
        <section class="files" v-if="(files || []).length">
          <c-info-tag
            v-for="(file, i) in files"
            :key="i"
            :class="{ file: !file.deletedAt }"
            icon="clip-attachment"
            icon-size="18"
            icon-gap="2"
            @click.native="onFileClick(file)"
          >
            {{ file.name }}
          </c-info-tag>
        </section>

        <section
          class="labels"
          v-if="Object.keys(labels).length"
        >
          <c-label-sign
            v-for="(label, key) in labels"
            :prepend-icon="label.icon"
            :theme="label.theme || theme"
            :key="key"
          >
            {{ key }}
          </c-label-sign>
        </section>

        <section class="information">
          <c-icon
            v-if="!isRemovedUser && isDelivered"
            class="delivered"
            :class="{
              '-read': isRead,
            }"
            size="15"
            icon="done-check-2"
          />

          <c-typo
            v-if="!!time"
            class="time"
            type="body-1"
            opacity="base-30"
            color="text"
          >{{ formattedTime }}</c-typo>
        </section>
      </footer>
    </section>

    <c-preview
      :is-opened="showGallery && !isFileDeleted"
      :images="images"
      :remove="currentImage?.removable"
      :current-image="current"
      :pdf-preview-page-limit="pdfPreviewPageLimit"
      @next="current += 1"
      @previous="current -= 1"
      @close="onClosePreview"
      @download="onDownload(currentImage)"
      @delete="onRemove(currentImage)"
      @click.native.prevent
    />
  </div>
</template>

<script>
import CAvatar from '@convenia/components/CAvatar'
import CTypo from '@convenia/components/CTypo'
import CIcon from '@convenia/components/CIcon'
import CInfoListTooltip from '@convenia/components/CInfoListTooltip'
import CActionsButton from '@convenia/components/CActionsButton'
import CFormBuilder from '@convenia/components/CFormBuilder/index.vue'
import CInfoTag from '@convenia/components/CInfoTag'
import CLabelSign from '@convenia/components/CLabelSign/CLabelSign.vue'
import CPreview from '@convenia/components/CPreview'
import CMarkdownMessage from '@convenia/components/CMarkdownMessage'
import tooltip from '@convenia/components/directives/modules/tooltip'
import { dayjs, props as utilProps, propsValidator } from '@convenia/utils'
import { MediaQuery } from '@convenia/mixins'

export default {
  name: 'CChatBubble',

  mixins: [ MediaQuery ],

  directives: { tooltip },

  components: {
    CAvatar,
    CTypo,
    CIcon,
    CInfoListTooltip,
    CActionsButton,
    CFormBuilder,
    CInfoTag,
    CLabelSign,
    CPreview,
    CMarkdownMessage,
  },

  props: {
    /**
     * An object representing the user details.
     * @type {Object}
     * @property {string} name - The name of the user.
     * @property {string} image - The URL of the user's image.
     */
    user: {
      type: Object,
      default: () => ({}),
    },

    /**
     * The message to be displayed.
     * @type {string}
     */
    message: {
      type: String,
      default: '',
    },
    enableMarkdown: {
      type: Boolean,
      default: false,
    },

    /**
     * @property {Object} labels - An object containing labels for the chat bubble.
     * @default {}
     */
    labels: {
      type: Object,
      default: () => ({}),
      validator (value) {
        const keys = [ 'icon' ]

        return Object.values(value).every((child) => {
          return keys.every(
            (key) => key in child
          )
        })
      },
    },

    /**
     * The time the message was sent.
     * @type {string|Date|number}
     */
    time: {
      type: [ String, Date, Number ],
      default: null,
    },

    /**
     * An array of files to be displayed.
     *
     * @typedef {Object} Item
     * @property {string} id - The unique identifier for the item.
     * @property {string} name - The name of the item.
     *
     * @type {Item[]}
     */
    files: {
      type: Array,
      default: () => [],
    },

    /**
     * A flag to indicate if the time should be displayed with the date.
     * @type {boolean}
     */
    isTimeWithDate: {
      type: Boolean,
      default: false,
    },

    /**
     * A flag to indicate if the message was sent by the current user.
     * @type {boolean}
     */
    isCurrentUser: {
      type: Boolean,
      default: false,
    },

    /**
     * A flag to indicate if the user was removed.
     * @type {boolean}
     */
    isRemovedUser: {
      type: Boolean,
      default: false,
    },

    /**
     * A flag to indicate if the message was delivered.
     * @type {boolean}
     */
    isDelivered: {
      type: Boolean,
      default: false,
    },

    /**
     * A flag to indicate if the message was read.
     * @type {boolean}
     */
    isRead: {
      type: Boolean,
      default: false,
    },

    /**
     * A flag to indicate if the message was edited.
     * @type {boolean}
     */
    isEdited: {
      type: Boolean,
      default: false,
    },

    /**
     * A flag to indicate whether the message has buttons.
     * @type {Item[]}
     */
    actions: {
      type: Array,
      default: () => []
    },

    /**
     * List of fields errors.
     */
    errors: {
      type: Object,
      default: () => ({})
    },

    /**
     * A flag to indicate whether there is editing.
     * @type {boolean}
     */
    hasEdition: {
      type: Boolean,
      default: false,
    },

    /**
     * A flag to indicate whether the comment was written by the logged in user.
     * @type {boolean|null}
     */
    isLoggedUser: {
      type: [ Boolean, null ],
      default: null,
    },

    /**
     * A flag to indicate whether the user has been terminated from the company.
     * @type {boolean}
     */
    isActiveUser: {
      type: Boolean,
      default: true,
    },

    /**
     * The page limit for PDF documents with more than one page.
     */
    pdfPreviewPageLimit: {
      type: Number,
      default: 30
    },

    /**
    * The theme of the chat bubble.
     */
    theme: utilProps.theme,

    /**
     * Some information related to the message.
     * @type {Item[]}
     */
    messageInfo: {
      type: Array,
      default: () => [],
    },

    /**
     * Information related to users of another business unit.
     * @type {Item[]}
     */
    companyInfo: {
      type: Array,
      default: () => [],
    },

    /**
     * A flag to indicate if the user is anonymous.
     * @type {boolean}
     */
    isAnonymous: {
      type: Boolean,
      default: false,
    },

    /**
     * Check if it can have the actions.
     * @type {boolean}
     */
    canHaveActions: {
      type: Boolean,
      default: true,
    },
  },

  data: () => ({
    showGallery: false,
    current: 0,
    newValue: '',
    text: 'Aperte <enter> para enviar, <shift+enter> para uma nova linha e <Esc> para cancelar.',
    schema: {
      comment: {
        type: 'text',
        textArea: true,
        maxlength: 1000,
        placeholder: 'Digite seu comentário',
      },
    },
    isFileDeleted: false,
    showTooltip: false,
    tooltipTarget: null,
    infoList: [],
  }),

  emits: [
    /**
     * Emits when a file is clicked.
     */
    'click:file',

    /**
     * Emits when the cancel button is clicked.
     */
    'action:cancel',

    /**
     * Emits when the save button is clicked.
     */
    'action:save',

    /**
     * Emits when a file is downloaded.
     */
    'preview:file:download',

    /**
     * Emits when a file is removed.
     */
    'preview:file:remove',

    /**
     * Emits when a file error occurs.
     */
    'preview:file:error',

    /**
     * Emits when the preview is closed.
     */
    'preview:file:close',

    /**
     * Emits when the preview is toggled.
     */
    'preview:toggle',
  ],

  computed: {
    classes () {
      return [ 'c-chat-bubble', {
        '-current-user': this.isCurrentUser,
        [`-${this.theme}`]: propsValidator.isValidTheme(this.theme)
      } ]
    },

    userName () {
      if (this.isRemovedUser)
        return 'Usuário excluído'

      if (this.isAnonymous)
        return 'Anônimo'

      return `${this.user?.name} ${this.isActiveUser ? '' : '(desativado)'}` || ''
    },

    userInitials () {
      return this.isRemovedUser || this.isAnonymous ? '' : this.user?.name
    },

    userImage () {
      return this.isRemovedUser || this.isAnonymous ? null : this.user?.image
    },

    formattedTime () {
      const isEdited = this.isEdited ? '[Editado em] DD/MM/YYYY' : 'DD/MM/YYYY'
      const format = this.isTimeWithDate ? `${isEdited} [às] HH:mm` : 'HH:mm'

      return dayjs(this.time).format(format)
    },

    userAction () {
      return this.actions.length && !this.isCurrentUser &&
        !this.isRemovedUser && typeof this.isRead === 'boolean' &&
        typeof this.isLoggedUser === 'boolean' && this.canHaveActions
    },

    value: {
      get () {
        return { comment: this.message || '' }
      },

      set (newValue) {
        this.newValue = newValue
      },
    },

    actionClasses () {
      return this.isRead || !this.isLoggedUser ? 'no-actions' : this.hasEdition
        ? 'edit-actions' : 'list-actions'
    },

    footerClasses () {
      return [
        'footer',
        { '-has-labels': Object.keys(this.labels).length }
      ]
    },

    formattedMessage () {
      const NEWLINE_REGEX = /\r\n|\r|\n/
      if (!this.message) {
        return [ this.files?.length > 1 ? 'Documentos anexados' : 'Documento anexado' ]
      }
      return this.message.split(NEWLINE_REGEX)
    },

    shouldRenderMarkdown () {
      return this.enableMarkdown && this.hasMarkdown
    },

    hasMarkdown () {
      if (!this.message) return false
      const markdownPattern =
      /(\*\*|__|~~|`|```|\[[^\]]+\]\([^)]+\)|(^|\n)\s*[-*+]\s+|(^|\n)\s*\d+\.\s+)/
      return markdownPattern.test(this.message)
    },

    images () {
      return (this.files || [])
        .filter(file => !file.deletedAt)
        .map(file => ({
          ...file,
          src: file.preview,
          label: file.name
        }))
    },

    currentImage () {
      return this.images[this.current]
    }
  },

  watch: {
    showGallery (value) {
      this.$emit('preview:toggle', value)
    }
  },

  methods: {
    onFileClick (file) {
      if (!file.preview) {
        this.$emit('click:file', file)
        return
      }

      const index = this.images.findIndex(image => file.preview === image.src)

      if (index === -1) return

      this.showGallery = true
      this.isFileDeleted = file?.deletedAt
      this.current = index
    },

    async onDownload (file = {}) {
      const url = await this.resolve(file)
      url && this.open({ ...file, url })
    },

    async resolve ({ url }) {
      try {
        return typeof url === 'function'
          ? await url()
          : url
      } catch (e) {
        this.$emit('preview:file:error')
      }
    },

    async open ({ openMethod, ...file }) {
      try {
        openMethod
          ? openMethod(file)
          : window.open(file.url, '_blank')
        this.$emit('preview:file:download', file)
      } catch (e) {
        this.$emit('preview:file:error')
      }
    },

    onClosePreview () {
      this.showGallery = false
      this.$emit('preview:file:close')
    },

    onRemove (file) {
      this.showGallery = false
      this.$emit('preview:file:remove', file)
    },

    completeEdition (value) {
      const { comment: newMessage } = value || {}
      const comment = newMessage === ''
        ? '' : newMessage || this.message

      this.$emit('action:save', comment.trim())
    },

    handleEnter (event) {
      if (event?.shiftKey) return

      event?.preventDefault()
      this.triggerFormSubmit()
    },

    closeEdition () {
      this.newValue = this.message
      this.$emit('action:cancel', this.message)
    },

    triggerFormSubmit () {
      this.$refs.form.onSubmit()
    },

    formFocus () {
      this.$nextTick(() => {
        this.$refs.form?.$refs?.fields?.[0]?.$el?.querySelector('textarea')?.focus()
      })
    },

    onShowTooltip (event, infoList) {
      this.showTooltip = true
      this.tooltipTarget = event.target
      this.infoList = infoList
    },

    onHideTooltip () {
      this.showTooltip = false
      this.tooltipTarget = null
    }
  },
}
</script>

<style lang="scss">
@import './_mixins.scss';

$chat-bubble-message-color: #868CA1;

.c-chat-bubble {
  --c-chat-bubble-avatar-margin-top: 16px;
  --c-chat-bubble-arrow-size: 13px;
  --c-chat-bubble-arrow-border-radius: 3px;

  %chat-bubble-arrow-common {
    content: '';
    position: absolute;
    top: calc(var(--c-chat-bubble-avatar-margin-top) + 15px);
    width: 0;
    height: 0;
    border-top: calc(var(--c-chat-bubble-arrow-size) / 2) solid transparent;
    border-bottom: calc(var(--c-chat-bubble-arrow-size) / 2) solid transparent;
  }

  display: flex;
  align-items: flex-start;
  gap: 20px;

  &:hover > .content > .name-header > .list-actions { visibility: visible; }

  @include generate-themes();

  & > .avatar {
    margin-top: var(--c-chat-bubble-avatar-margin-top);
  }

  & > .content {
    display: flex;
    flex-direction: column;
    padding: 20px;
    border-radius: var(--base-border-radius);
    width: 100%;
    max-width: calc(100% - 60px);
    position: relative;
    @include responsive(xs-mobile, tablet) { padding: 15px; }

    & > .name-header {
      display: grid;
      grid-template-columns: 1fr auto;

      & > .user {
        grid-area: 1 / 1 / 2 / 2;
        margin-bottom: 5px;
        display: flex;
        align-items: center;
        width: 100%;
        overflow: hidden;

        & > .user-name {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        & > .info {
          display: flex;
          align-items: center;

          &.message-info {
            margin-left: 8px;
          }

          &:not(.message-info) {
            margin-left: 5px;
          }

          & > .icon-info {
            stroke: var(--color-text-base-30);
          }
        }
      }

      & > .list-actions {
        grid-area: 1 / 2 / 3 / 3;
        visibility: hidden;
      }

      & > .edit-actions {
        grid-area: 1 / 2 / 3 / 3;
        margin-left: 50px;

        @media only screen and (max-width: 1024px) {
          margin-left: 20px;
        }
      }

      & > .no-actions {
        grid-area: 1 / 2 / 3 / 3;
        visibility: hidden;
      }

      & > .form {
        grid-area: 2 / 1 / 3 / 2;
        margin-top: 5px;

        @include responsive(xs-mobile, mobile) {
          margin-top: 0;
          max-width: 41vw;
        }

          & > .c-shadowed > .wrapper > .form-fields {
          padding: 0px;

          & > .c-form-field.-label-top {
            max-width: 100%;
          }

          & > .c-form-field {
            margin: 0;
            grid-template-columns: 100%;

            & > .input-wrapper > .c-input-raw > .c-input-abstract {
              padding-right: 20px;
            }
          }
        }
      }

      & > .form-text {
        grid-area: 3 / 1 / 4 / 2;
        justify-self: end;
        margin-top: 10px;
      }

      & > .group-message {
        grid-area: 2 / 1 / 3 / 2;
        margin-bottom: 22px;
        word-break: break-word;
        line-height: 18.6px;

        & > .message {
          color: $chat-bubble-message-color;
        }
      }
    }

    & > .footer {
      display: flex;
      align-items: center;
      max-width: calc(100% - 40px);

      &.-has-labels {
        margin-top: 10px;
      }

      & > .labels {
        display: flex;
        justify-content: center;
        position: absolute;
        bottom: 10px;
      }

      & > .files {
        flex-flow: row wrap;
        display: flex;
        gap: 10px;
        width: 100%;
        max-width: 100%;
        padding-right: 20px;

        @include responsive(xs-mobile, tablet) { margin-bottom: 15px; }

        & > .file {
          cursor: pointer;
          max-width: inherit;
        }
      }

      & > .information {
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        bottom: 10px;
        right: 15px;

        & > .delivered {
          margin-right: 5px;
          stroke: rgba(var(--color-text-base-rgb), 0.3);
        }

        & > .delivered.-read {
          stroke: rgba(var(--color-info-base-rgb), 0.8);
        }

        & > .time {
          margin-right: 4px;
        }
      }
    }
  }

  &:not(.-current-user) {
    & > .content {
      background-color: color-var(text, base-02);

      &::before {
        @extend %chat-bubble-arrow-common;
        left: 0;
        transform: translateX(-100%);
        border-right: calc(var(--c-chat-bubble-arrow-size) / 2) solid var(--color-text-base-02);
      }
    }
  }

  &.-current-user {
    flex-direction: row-reverse;

    & > .content {
      background-color: rgba(lighten($primary-color, 36%), .3);

      &::after {
        @extend %chat-bubble-arrow-common;
        right: 0;
        transform: translateX(100%);
        border-left: calc(var(--c-chat-bubble-arrow-size) / 2) solid rgba(lighten($primary-color, 36%), .3);
      }
    }
  }
}
</style>
